import { createSlice } from "@reduxjs/toolkit";

import { ORGANIZATION_ID_FIELD } from "Constants/constants";
import { setDeep } from "Libs/objectAccess";
import { normalize } from "Libs/utils";
import { getCommonError } from "Reducers/sliceFactory";

import {
  loadMultiVendorOrgsAction,
  loadOrganizationsAction,
  loadRefOrganizationsAction,
  loadOrganizationFailureAction,
  loadOrganizationAction,
  loadOrganizationStartAction
} from "./actions";
import { CreateOrganizationNamespace } from "./constants";
import {
  getOrganization,
  loadOrganization,
  createOrganization,
  deleteOrganization,
  loadOrganizationEstimate,
  updateOrganization
} from "./thunks";
import { loadOrganizationHandler } from "./util";

import type {
  OrganizationState,
  GetOrganizationPayloadFulfilled,
  LoadOrganizationPayloadFulfilled,
  GetOrganizationPayloadRejected,
  DeleteOrganizationFulfilled
} from "./types";
import type { Organization } from "@packages/client";
import type { PayloadAction } from "@reduxjs/toolkit";

export const initialState: OrganizationState = {
  loading: false,
  orgByDescriptionField: {},
  multiVendorOrgs: [],
  data: {},
  list: [],
  errors: undefined,
  update: {},
  inflight: {},
  _errors: {},
  [CreateOrganizationNamespace]: undefined,
  organizationLoadingError: undefined,
  deleted: {
    loading: false
  },
  organizationEstimate: {}
};

const organizationReducer = createSlice({
  name: "app/organization",
  initialState,
  reducers: {
    clearOrganizationError(state) {
      state.organizationLoadingError = undefined;
    },
    clearCreatedOrganization(state) {
      state[CreateOrganizationNamespace] = undefined;
    }
  },
  extraReducers: builder => {
    builder
      // GET ORGANIZATION
      // No getOrganization.pending - this is a helper function
      .addCase(
        getOrganization.fulfilled,
        (state, action: PayloadAction<GetOrganizationPayloadFulfilled>) => {
          const { shouldUpdateState, organization } = action.payload ?? {};
          if (shouldUpdateState) {
            state.loading = false;
            state.errors = undefined;
            if (organization) {
              setDeep(
                state,
                ["orgByDescriptionField", organization[ORGANIZATION_ID_FIELD]],
                organization
              );
              setDeep(state, ["data", organization.id], organization);
            }
          }
        }
      )
      .addCase(
        getOrganization.rejected,
        (state, action: PayloadAction<GetOrganizationPayloadRejected>) => {
          const { error, shouldUpdateState } = action.payload;
          if (shouldUpdateState) {
            state.loading = false;
            state.organizationLoadingError = error;
          }
        }
      )
      // LOAD ORGANIZATION
      .addCase(loadOrganization.pending, state => {
        state.loading = true;
      })
      .addCase(
        loadOrganization.fulfilled,
        (state, action: PayloadAction<LoadOrganizationPayloadFulfilled>) => {
          loadOrganizationHandler(state, action);
        }
      )
      .addCase(
        loadOrganization.rejected,
        (state, action: PayloadAction<Error>) => {
          state.loading = false;
          state.organizationLoadingError = action.payload;
        }
      )
      // CREATE ORGANIZATION
      .addCase(createOrganization.pending, state => {
        setDeep(state, ["inflight", CreateOrganizationNamespace], true);

        setDeep(state, ["_errors", CreateOrganizationNamespace], undefined);
      })
      .addCase(
        createOrganization.fulfilled,
        (state, action: PayloadAction<Organization>) => {
          setDeep(state, ["orgByDescriptionField"], {
            ...state.orgByDescriptionField,
            ...normalize([action.payload], ORGANIZATION_ID_FIELD)
          });

          setDeep(state, ["data"], {
            ...state.data,
            ...normalize([action.payload], "id")
          });

          setDeep(state, ["inflight", CreateOrganizationNamespace], false);

          setDeep(state, [CreateOrganizationNamespace], action.payload);

          setDeep(state, ["list"], [...state.list, action.payload.id]);

          setDeep(
            state,
            ["multiVendorOrgs"],
            [...state.multiVendorOrgs, action.payload]
          );
        }
      )
      .addCase(createOrganization.rejected, (state, action) => {
        setDeep(state, ["inflight", CreateOrganizationNamespace], false);

        setDeep(
          state,
          ["_errors", CreateOrganizationNamespace],
          action.payload
        );
      })
      // DELETE ORGANIZATION
      .addCase(deleteOrganization.pending, state => {
        setDeep(state, ["deleted"], {
          ...state.deleted,
          loading: true
        });
      })
      .addCase(
        deleteOrganization.fulfilled,
        (state, action: PayloadAction<DeleteOrganizationFulfilled>) => {
          if (action.payload)
            setDeep(state, ["deleted"], {
              ...state.deleted,
              [action.payload.id]: action.payload,
              loading: false
            });
        }
      )
      .addCase(deleteOrganization.rejected, (state, action) => {
        state.errors = getCommonError(action.payload)?.error.message;

        setDeep(state, ["deleted"], {
          ...state.deleted,
          loading: false
        });
      })
      // LOAD ORGANIZATION ESTIMATE
      .addCase(loadOrganizationEstimate.pending, state => {
        state.loading = true;
      })
      .addCase(loadOrganizationEstimate.fulfilled, (state, action) => {
        const { organizationId, current, future } = action.payload ?? {};

        state.loading = false;
        if (organizationId && current && future) {
          setDeep(state, ["organizationEstimate", organizationId], {
            current,
            future
          });
        }
      })
      .addCase(loadOrganizationEstimate.rejected, (state, action) => {
        state.loading = false;
        state.errors = getCommonError(action.payload)?.error.message;
      })
      // UPDATE ORGANIZATION
      .addCase(updateOrganization.pending, state => {
        state.loading = true;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.errors = undefined;

        if (action?.payload?.organization) {
          setDeep(state, ["update"], {
            ...state.update,
            [action.payload.organization.id]: true
          });

          state.list = [...state.list, action.payload.organization.id];

          setDeep(state, ["orgByDescriptionField"], {
            ...state.orgByDescriptionField,
            ...normalize([action.payload.organization], ORGANIZATION_ID_FIELD)
          });

          setDeep(state, ["data"], {
            ...state.data,
            ...normalize([action.payload.organization], "id")
          });
        }
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.loading = false;
        state.errors = getCommonError(action.payload)?.error.message;
      })
      // LOAD MULTI-VENDOR ** ACTION
      .addCase(
        loadMultiVendorOrgsAction,
        (state, action: PayloadAction<Organization[] | undefined>) => {
          if (action.payload) {
            state.multiVendorOrgs = action.payload;
          }
        }
      )
      // LOAD ORGANIZATIONS START ** ACTION
      .addCase(loadOrganizationStartAction, state => {
        state.loading = true;
      })
      // LOAD ORGANIZATIONS ** ACTION
      .addCase(
        loadOrganizationsAction,
        (state, action: PayloadAction<Organization[] | undefined>) => {
          state.loading = false;
          state.errors = undefined;

          if (action.payload) {
            state.list = action.payload.map(organization => organization.id);

            setDeep(state, ["orgByDescriptionField"], {
              ...state.orgByDescriptionField,
              ...normalize(action.payload, ORGANIZATION_ID_FIELD)
            });

            setDeep(state, ["data"], {
              ...state.data,
              ...normalize(action.payload, "id")
            });
          }
        }
      )
      // LOAD ORGANIZATION ** ACTION
      .addCase(loadOrganizationAction, (state, action) => {
        loadOrganizationHandler(state, action);
      })
      // LOAD REF ORGANIZATIONS ** ACTION
      .addCase(
        loadRefOrganizationsAction,
        (state, action: PayloadAction<Organization[] | undefined>) => {
          if (action.payload) {
            state.list = action.payload.map(organization => organization.id);

            setDeep(state, ["orgByDescriptionField"], {
              ...state.orgByDescriptionField,
              ...normalize(action.payload, ORGANIZATION_ID_FIELD)
            });

            setDeep(state, ["data"], {
              ...state.data,
              ...normalize(action.payload, "id")
            });
          }
        }
      )
      // CREATE ORGANIZATION FAILURE ** ACTION
      .addCase(loadOrganizationFailureAction, (state, action) => {
        state.loading = false;
        state.organizationLoadingError = action.payload;
      });
  }
});

export default organizationReducer.reducer;

export const { clearOrganizationError, clearCreatedOrganization } =
  organizationReducer.actions;
